"use client";

import React, { useEffect, useRef, useState } from "react";
import SectionTitle from "./SectionTitle";

type MegaCard = {
    title: string;
    description: string;
};

type CaseStudiesNewMegaCardSectionProps = {
    cards?: MegaCard[];
    sectionTitle?: {
        title?: string;
        description?: string;
        align?: "left" | "center" | "right";
    };
};

const CaseStudiesNewMegaCardSection: React.FC<CaseStudiesNewMegaCardSectionProps> = ({
    cards = [],
    sectionTitle,
}) => {
    const rightSectionRef = useRef<HTMLDivElement>(null);
    const [clipStartX, setClipStartX] = useState(0);
    const [clipHeight, setClipHeight] = useState(0);

    // Update clip-path dynamically
    useEffect(() => {
        const updateClip = () => {
            if (rightSectionRef.current) {
                const rect = rightSectionRef.current.getBoundingClientRect();
                const containerLeft = rightSectionRef.current.offsetParent
                    ? (rightSectionRef.current.offsetParent as HTMLElement).getBoundingClientRect().left
                    : 0;

                setClipStartX(rect.left - containerLeft);
                setClipHeight(rect.height);
            }
        };

        updateClip();
        window.addEventListener("resize", updateClip);

        return () => window.removeEventListener("resize", updateClip);
    }, []);

    // Calculate bottom-left X offset for 24° angle
    const angleDeg = 12;
    const angleRad = (angleDeg * Math.PI) / 180;
    const offsetX = clipHeight * Math.tan(angleRad);

    return (
        <section className="w-full custom-container    relative  py-16 lg:py-24 xl:py-0  ">
            {/* Right-side Clip-path Background */}
            <div
                className="hidden 2xl:block absolute top-0 h-full   w-full pointer-events-none z-0 bg-[rgba(235,235,235,0.4)]"
                style={{
                    clipPath: `polygon(
                        ${clipStartX}px 0px,
                        100% 0px,
                        100% 100%,
                        ${clipStartX - offsetX}px 100%
                    )`,
                }}
            ></div>

            <div className="  grid grid-cols-12 gap-8 2xl:gap-16 relative items-center z-10">
                {/* Left Section */}
                <div className="col-span-12 2xl:col-span-4 2xl:max-w-xs  2xl:max-w-md">
                    {sectionTitle && (
                        <SectionTitle
                            title={sectionTitle.title ?? ""}
                            description={sectionTitle.description ?? ""}
                            align={sectionTitle.align ?? "left"}
                        />
                    )}
                </div>

                {/* Right Section */}
                <div ref={rightSectionRef} className="col-span-12 2xl:col-span-8 border border-[#ccc]/48 2xl:border-none">
                    <div className="w-full  2xl:bg-transparent grid grid-cols-6 auto-rows-auto relative z-10 ">
                        {/* Column Headings */}
                        <div className="col-span-2 px-2 md:px-6 lg:px-8 border-r-2 py-4 2xl:pt-12 border-[#ccc]/48 flex items-center">
                            <h4 className="text-[#272d57] text-base md:text-xl font-semibold">Growth Area</h4>
                        </div>

                        <div className="col-span-4 px-6 lg:px-8 border-r-0 py-4 2xl:pt-12 border-[#ccc]/48 flex items-center">
                            <h4 className="text-red-600 text-base md:text-xl font-semibold">What we delivered?</h4>
                        </div>

                        {/* Cards */}
                        {cards.map((card, index) => {
                            const isLast = index === cards.length - 1;

                            return (
                                <React.Fragment key={index}>
                                    {/* Title column */}
                                    <div
                                        className={`col-span-2 pl-2 md:pl-6 2xl:pl-0 pr-4 py-4 border border-[#ccc]/48 border-b-0 border-l-0 flex items-center ${isLast ? "pb-4" : ""
                                            }`}
                                    >
                                        <span className="w-2 h-2 hidden md:block bg-[#d53333] rounded-full mr-3 shrink-0"></span>

                                        <h3 className="text-[#272d57] text-base md:text-lg font-semibold leading-tight">
                                            {card.title}
                                        </h3>
                                    </div>

                                    {/* Description column */}
                                    <div
                                        className={`col-span-4 px-6 lg:px-8 py-4 border border-[#ccc]/48 border-b-0 border-r-0 flex items-center ${isLast ? "pb-4" : ""
                                            }`}
                                    >
                                        <p className="text-[#1b1b1b] text-sm md:text-base leading-tight">
                                            {card.description}
                                        </p>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CaseStudiesNewMegaCardSection;
